'use client';

import {
  faEarthAmerica,
  faGraduationCap,
  faNoteMedical,
} from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Fragment, ReactNode } from 'react';
import {
  EntityRatingCommentCount,
  Professional,
  Rating,
} from '@medprev-web-app/api-graphql-client';

import { ProfessionalAbout } from './professional-about.component';
import { ProfessionalServiceLocations } from './professional-service-locations.component';

import { EntitySection } from '@/app/(main)/(pages)/_components/entities/entity-section.component';
import {
  EntityTab,
  EntityTabContent,
  EntityTabList,
  EntityTabProvider,
  EntityTabTrigger,
} from '@/app/(main)/(pages)/_components/entities/entity-tab.component';
import { RatingCard } from '@/app/(main)/(pages)/_components/entities/entity-rating-card.component';
import { ItemList } from '@/app/(main)/(pages)/_components/entities/item-list.component';
import { formatExperiences } from '@/utils/professional/professional-license.util';
import { RatingComment } from '@/types';
import {
  EntityLoadMoreComments,
  LoadCommentsPage,
} from '@/app/(main)/(pages)/_components/entities/entity-load-more-comments.component';
import { CommentLoading } from '@/app/(main)/(pages)/_components/entities/entity-comment-loading.component';
import { cn } from '@/utils/cn-tw-merge';
import { PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE } from '@/app/(main)/(pages)/(institucional)/(index)/profissionais/_services/get-professional-profile-from-search-api.service';
import {
  formatProfessionalFormations,
  formatProfessionalLanguages,
  formatProfessionalTreatments,
} from '@/utils/professional/online-professional.utils';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { InPersonScheduleSource } from '@/utils/professional/extract-professional-location';

interface TabProfessionalProps {
  professional: Professional;
  countComments?: EntityRatingCommentCount | null;
  inPersonSchedules: InPersonScheduleSource[];
  hasTelehealth: boolean;
  initialComments: RatingComment[];
  hasMoreComments?: boolean;
  loadCommentsPage?: LoadCommentsPage;
  commentsSort?: ReactNode;
  commentsKey?: string;
  isSortingComments?: boolean;
  schedulesSection?: ReactNode;
}

function AboutSectionDivider() {
  return (
    <div className="h-6 flex items-center">
      <hr className="w-full border-gray-300" />
    </div>
  );
}

export function TabProfessional({
  professional,
  countComments,
  inPersonSchedules,
  hasTelehealth,
  initialComments,
  hasMoreComments,
  loadCommentsPage,
  commentsSort,
  commentsKey,
  isSortingComments,
  schedulesSection,
}: TabProfessionalProps) {
  const formattedFormations = formatProfessionalFormations(professional);

  const formattedExperiences = formatExperiences(professional);

  const formationAndExperiences = [
    ...formattedFormations,
    ...formattedExperiences,
  ];

  const formattedLanguages = formatProfessionalLanguages(professional);

  const formattedTreatments = formatProfessionalTreatments(professional);

  const hasServiceLocations = hasTelehealth || inPersonSchedules.length > 0;

  const hasComments = Boolean(countComments) && Number(countComments?.total) > 0;

  const aboutSections: { key: string; node: ReactNode }[] = [];

  if (hasServiceLocations) {
    aboutSections.push({
      key: 'service-locations',
      node: (
        <ProfessionalServiceLocations
          inPersonSchedules={inPersonSchedules}
          hasTelehealth={hasTelehealth}
        />
      ),
    });
  }

  if (professional.description) {
    aboutSections.push({
      key: 'about-me',
      node: (
        <EntitySection
          title="Sobre mim"
          id="about-me"
          className="my-0 py-4"
          titleStyles="leading-6 sm:leading-7"
          removeHr
        >
          <ProfessionalAbout professional={professional} />
        </EntitySection>
      ),
    });
  }

  if (formationAndExperiences.length > 0) {
    aboutSections.push({
      key: 'formations',
      node: (
        <EntitySection
          icon={faGraduationCap}
          title="Formação & experiência"
          classNameIcon="sm:mt-0.5"
          className="my-0 py-4"
          titleStyles="leading-6 sm:leading-7"
          removeHr
        >
          <ItemList
            items={formationAndExperiences}
            dataTestID="formations"
            data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_EDUCATION_AND_EXPERIENCE}
          />
        </EntitySection>
      ),
    });
  }

  if (formattedTreatments.length > 0) {
    aboutSections.push({
      key: 'treatments',
      node: (
        <EntitySection
          icon={faNoteMedical}
          title="Doenças, sintomas ou condições"
          classNameIcon="sm:mt-0.5"
          className="my-0 py-4"
          titleStyles="leading-6 sm:leading-7"
          removeHr
        >
          <ItemList
            items={formattedTreatments}
            dataTestID="treatments"
            data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_DISEASES}
          />
        </EntitySection>
      ),
    });
  }

  if (formattedLanguages.length > 0) {
    aboutSections.push({
      key: 'languages',
      node: (
        <EntitySection
          icon={faEarthAmerica}
          title={formattedLanguages.length > 1 ? 'Idiomas' : 'Idioma'}
          classNameIcon="sm:mt-0.5"
          className="my-0 py-4"
          titleStyles="leading-6 sm:leading-7"
          removeHr
        >
          <ItemList
            items={formattedLanguages}
            dataTestID="languages"
            data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_LANGUAGES}
          />
        </EntitySection>
      ),
    });
  }

  return (
    <>
      <EntityTabProvider>
        <EntityTab
          defaultValue="about"
          tabs={['about', 'rating']}
        >
          <EntityTabList className="sticky top-0 bg-white pt-2 pb-4">
            <EntityTabTrigger
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TAB_ABOUT}
              value="about"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_ABOUT_TAB}
            >
              Sobre
            </EntityTabTrigger>
            <EntityTabTrigger
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TAB_RATING}
              value="rating"
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_REVIEWS_TAB}
            >
              Avaliações
            </EntityTabTrigger>
          </EntityTabList>

          <div data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_TABS_CONTAINER}>
            <EntityTabContent value="about">
              {aboutSections.map((section, index) => (
                <Fragment key={section.key}>
                  {index > 0 && <AboutSectionDivider />}
                  {section.node}
                </Fragment>
              ))}
            </EntityTabContent>

            {schedulesSection && (
              <div className="xl:hidden">
                {aboutSections.length > 0 && <AboutSectionDivider />}
                {schedulesSection}
              </div>
            )}

            <EntityTabContent value="rating">
              {aboutSections.length > 0 && <AboutSectionDivider />}

              <EntitySection className={cn('my-0 py-4', commentsSort && 'pb-0')} removeHr>
                <RatingCard
                  rating={professional.rating as Rating}
                  countComments={countComments}
                  commentsSort={commentsSort}
                />
              </EntitySection>

              {hasComments && (isSortingComments
                ? (
                    <div aria-busy data-testid={DataTestIdEnum.ENTITY_COMMENTS_LOADING}>
                      {Array.from({ length: PROFESSIONAL_PROFILE_COMMENTS_PAGE_SIZE }).map((_, index) => (
                        <CommentLoading key={index} />
                      ))}
                    </div>
                  )
                : (
                    <EntityLoadMoreComments
                      key={commentsKey}
                      countComments={countComments}
                      professionalId={professional.id as string}
                      initialComments={initialComments}
                      loadPage={loadCommentsPage}
                      hasMore={hasMoreComments}
                      className="mb-0 md:mb-0"
                      commentClassName="first:mt-3"
                      analyticsEventShowMore={AnalyticsEnum.PROFESSIONAL_PROFILE_EXPAND_COMMENT}
                      analyticsEventShowMoreComments={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_SHOW_MORE_COMMENTS}
                    />
                  ))}
            </EntityTabContent>
          </div>
        </EntityTab>
      </EntityTabProvider>
    </>
  );
}
